import React, { useState, useCallback } from 'react'
import styled, { css } from 'styled-components'
import { Medium, Tablet, MqTablet, MqDesktop } from 'streamr-ui/css/consts'
import Swipeable from '~shared/Swipeable'
import tickIcon from './assets/tick.svg'
import crossIcon from './assets/cross.svg'
import Topic from '~shared/Topic'
import Container from '~shared/Container'
import Display from '~shared/Display'
import SliderNav from '~shared/SliderNav'
import Spacer from '~shared/Spacer'

const difficultTheme = {
    background: '#D90C25',
}

const preSharedKeyTheme = {
    background: '#6240AF',
}

const bestEffortTheme = {
    background: '#0324FF',
}

const Qualifier = styled.div`
    width: 6px;
    height: 6px;
    border-radius: 3px;
    background-color: ${({ theme }) => theme.background};
`

Object.assign(Qualifier, {
    difficult: difficultTheme,
    preSharedKey: preSharedKeyTheme,
    bestEffort: bestEffortTheme,
})

const UnstyledIcon = ({ tick, cross, qualifier, ...props }) => (
    <div {...props}>
        {!!tick && <img src={tickIcon} alt="Yes" />}
        {!!cross && <img src={crossIcon} alt="No" />}
        {!!qualifier && <Qualifier theme={qualifier} />}
    </div>
)

const Icon = styled(UnstyledIcon)`
    position: relative;
    display: inline-block;
    width: 32px;
    height: 32px;

    img {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);

        ${({ tick }) =>
            !!tick &&
            css`
                width: 16px;
            `}

        ${({ cross }) =>
            !!cross &&
            css`
                width: 10px;
            `}
    }

    ${Qualifier} {
        position: absolute;

        ${({ tick }) =>
            !!tick &&
            css`
                top: 18px;
                right: 4px;
            `}

        ${({ cross }) =>
            !!cross &&
            css`
                top: 6px;
                right: 2px;
            `}
    }
`

const LegendItem = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;

    ${Qualifier} + * {
        margin-left: 0.5rem;
    }
`

const Legend = styled.div`
    display: flex;
    flex-direction: row;
    font-size: 0.75rem;
    line-height: 1rem;
    padding: 0 0.5rem;

    ${LegendItem} + ${LegendItem} {
        margin-left: 1rem;
    }

    @media ${MqTablet} {
        ${LegendItem} + ${LegendItem} {
            margin-left: 2rem;
        }
    }
`

const TableContainer = styled(Container)`
    ${Legend} {
        margin-top: 1rem;
    }

    @media ${MqTablet} {
        ${Legend} {
            margin-top: 1.5rem;
        }
    }
`

const qualifierNames = {
    bestEffort: 'Best-effort',
    preSharedKey: 'With pre-shared AES key',
    difficult: 'Difficult to implement',
}

const tableData = {
    head: ['Streamr', 'PubNub', 'Google Firebase', 'Matrix'],
    rows: [
        {
            title: 'Architecture',
            columns: [
                {
                    text: 'Decentralised',
                },
                {
                    text: 'Distributed',
                },
                {
                    text: 'Distributed',
                },
                {
                    text: 'Federated',
                },
            ],
        },
        {
            title: 'Message Ordering',
            columns: [
                {
                    hasFeature: true,
                },
                {
                    hasFeature: false,
                    qualifier: 'bestEffort',
                },
                {
                    hasFeature: false,
                    qualifier: 'bestEffort',
                },
                {
                    hasFeature: true,
                },
            ],
        },
        {
            title: 'Message completeness',
            columns: [
                {
                    hasFeature: true,
                },
                {
                    hasFeature: false,
                    qualifier: 'bestEffort',
                },
                {
                    hasFeature: false,
                    qualifier: 'bestEffort',
                },
                {
                    hasFeature: true,
                },
            ],
        },
        {
            title: 'Message signing',
            columns: [
                {
                    hasFeature: true,
                },
                {
                    hasFeature: false,
                },
                {
                    hasFeature: false,
                },
                {
                    hasFeature: true,
                    qualifier: 'difficult',
                },
            ],
        },
        {
            title: 'E2E Encryption',
            columns: [
                {
                    hasFeature: true,
                },
                {
                    hasFeature: true,
                    qualifier: 'preSharedKey',
                },
                {
                    hasFeature: false,
                },
                {
                    hasFeature: true,
                    qualifier: 'difficult',
                },
            ],
        },
        {
            title: 'Data monetisation',
            columns: [
                {
                    hasFeature: true,
                },
                {
                    hasFeature: false,
                },
                {
                    hasFeature: false,
                },
                {
                    hasFeature: false,
                },
            ],
        },
        {
            title: 'Storage',
            columns: [
                {
                    hasFeature: true,
                },
                {
                    hasFeature: true,
                },
                {
                    hasFeature: true,
                },
                {
                    hasFeature: true,
                },
            ],
        },
        {
            title: 'License',
            columns: [
                {
                    text: 'Open source',
                },
                {
                    text: 'Proprietary',
                },
                {
                    text: 'Proprietary',
                },
                {
                    text: 'Apache V2',
                },
            ],
        },
        {
            title: 'Governance',
            columns: [
                {
                    text: 'DAO',
                },
                {
                    text: 'Company',
                },
                {
                    text: 'N/A',
                },
                {
                    text: 'Foundation',
                },
            ],
        },
        {
            title: 'Identity',
            columns: [
                {
                    text: (
                        <>
                            <Display mobile="none" desktop="inline">
                                Ethereum public key
                            </Display>
                            <Display mobile="inline" desktop="none">
                                Eth public key
                            </Display>
                        </>
                    ),
                },
                {
                    text: 'Email',
                },
                {
                    text: 'Email',
                },
                {
                    text: (
                        <>
                            <Display mobile="none" desktop="inline">
                                Email + phone number
                            </Display>
                            <Display mobile="inline" desktop="none">
                                Email + phone
                            </Display>
                        </>
                    ),
                },
            ],
        },
    ],
    mobileLegend: [['bestEffort', 'preSharedKey'], ['bestEffort'], ['difficult']],
}

const MobileSlides = styled.div`
    display: grid;
    align-items: start;

    > * {
        grid-row: 1;
        grid-column: 1;
        opacity: 0;
        transition: 300ms ease-in-out opacity;

        &[data-visible='true'] {
            opacity: 1;
        }
    }

    @media ${MqTablet} {
        display: none;
    }
`

const UnstyledNetworksCompared = (props) => {
    const [mobileSlide, setMobileSlide] = useState(0)

    const next = useCallback(() => {
        setMobileSlide(Math.max(0, mobileSlide - 1))
    }, [mobileSlide])

    const prev = useCallback(() => {
        setMobileSlide(Math.min(2, mobileSlide + 1))
    }, [mobileSlide])

    return (
        <div {...props}>
            <Container>
                <Topic.Title $appear>
                    <a href="#networks-compared">Networks compared</a>
                </Topic.Title>
                <Topic.Paragraph $appear>
                    Understanding how the Streamr network differs from other solutions can help to
                    clarify the strengths of a truly decentralised real-time data network.
                </Topic.Paragraph>
            </Container>
            <Display as={TableContainer} mobile="none" tablet="block">
                <table>
                    <thead>
                        <tr $appearIntersection>
                            <th>&nbsp;</th>
                            {tableData.head.map((name) => (
                                <th key={name}>
                                    <div $appearDirection="none" $appearDelay="0.01">
                                        {name}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {tableData.rows.map(({ title, columns }) => (
                            <tr key={title} $appearIntersection>
                                <th>
                                    <div $appearDirection="none" $appearDelay="0.01">
                                        {title}
                                    </div>
                                </th>
                                {columns.map(({ text, hasFeature, qualifier }, columnIndex) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <td key={`${title}-${columnIndex}`}>
                                        <div $appearDirection="none" $appearDelay="0.01">
                                            {text}
                                            {hasFeature !== undefined && (
                                                <Icon
                                                    tick={!!hasFeature}
                                                    cross={!hasFeature}
                                                    qualifier={!!qualifier && Qualifier[qualifier]}
                                                />
                                            )}
                                        </div>
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
                <Legend>
                    {Object.keys(qualifierNames).map((qualifier) => (
                        <LegendItem key={qualifier} $appear>
                            <Qualifier theme={Qualifier[qualifier]} />
                            <div>{qualifierNames[qualifier]}</div>
                        </LegendItem>
                    ))}
                </Legend>
            </Display>
            <Swipeable onSwipedLeft={prev} onSwipedRight={next}>
                <TableContainer>
                    <MobileSlides>
                        {(() => {
                            const [streamr, ...rest] = tableData.head

                            return rest.map((name, index) => (
                                <div data-visible={index === mobileSlide} key={name}>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>&nbsp;</th>
                                                <th>{streamr}</th>
                                                <th>{name}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tableData.rows.map(({ title, columns }) => (
                                                <tr key={title}>
                                                    <th>{title}</th>
                                                    {[columns[0], columns[index + 1]].map(
                                                        (
                                                            { text, hasFeature, qualifier },
                                                            columnIndex,
                                                        ) => (
                                                            // eslint-disable-next-line react/no-array-index-key
                                                            <td
                                                                key={`${name}-${index}-${columnIndex}`}
                                                            >
                                                                {text}
                                                                {hasFeature !== undefined && (
                                                                    <Icon
                                                                        tick={!!hasFeature}
                                                                        cross={!hasFeature}
                                                                        qualifier={
                                                                            !!qualifier &&
                                                                            Qualifier[qualifier]
                                                                        }
                                                                    />
                                                                )}
                                                            </td>
                                                        ),
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <Legend>
                                        {tableData.mobileLegend[index].map((qualifier) => (
                                            <LegendItem key={qualifier}>
                                                <Qualifier theme={Qualifier[qualifier]} />
                                                <div>{qualifierNames[qualifier]}</div>
                                            </LegendItem>
                                        ))}
                                    </Legend>
                                </div>
                            ))
                        })()}
                    </MobileSlides>
                    <Display tablet="none">
                        <Spacer head="3rem">
                            <SliderNav
                                theme={SliderNav.WhiteTheme}
                                current={mobileSlide}
                                size={3}
                                onChange={setMobileSlide}
                            />
                        </Spacer>
                    </Display>
                </TableContainer>
            </Swipeable>
        </div>
    )
}

const NetworksCompared = styled(UnstyledNetworksCompared)`
    ${Topic.Title},
    ${Topic.Paragraph} {
        margin: 0 auto;
        max-width: 610px;
        text-align: left;
    }

    ${Topic.Title} {
        margin-bottom: 1.75rem;
    }

    ${Container} {
        padding-right: 1.5rem;
    }

    table {
        margin-top: 3.5rem;
        border-collapse: separate;
        border-spacing: 0px;
        border: 1px solid #525252;
        border-radius: 4px;
        width: 100%;
        color: #ffffff;

        thead {
            th {
                border-bottom: 1px solid #525252;
                background-color: #000000;
                text-align: center;
                font-size: 0.75rem;
                font-weight: ${Medium};

                :first-child {
                    border-top-left-radius: 4px;
                }

                :last-child {
                    border-top-right-radius: 4px;
                }
            }
        }

        tbody {
            font-size: 0.75rem;

            th {
                text-align: left;
                padding-left: 16px;
                font-weight: ${Medium};
            }

            td {
                text-align: center;
            }

            tr:nth-child(even) {
                td,
                th {
                    background-color: #000000;
                }
            }

            tr:last-child {
                > :first-child {
                    border-bottom-left-radius: 4px;
                }

                > :last-child {
                    border-bottom-right-radius: 4px;
                }
            }
        }

        td,
        th {
            height: 48px;
            box-sizing: border-box;
        }

        th + th,
        th + td,
        td + td {
            border-left: 1px solid #525252;
        }

        tr + tr {
            td,
            th {
                border-top: 1px solid #525252;
            }
        }
    }

    ${TableContainer} {
        padding: 0 1rem;
    }

    @media (max-width: ${Tablet - 1}px) {
        tbody {
            td {
                width: 104px;
            }
        }
    }

    @media ${MqTablet} {
        ${Topic.Title},
        ${Topic.Paragraph} {
            text-align: center;
        }

        ${Container} {
            padding-right: 2rem;
        }

        tbody {
            td:nth-child(2) {
                width: 150px;
            }

            td {
                width: 134px;
            }
        }

        ${TableContainer} {
            box-sizing: border-box;
            max-width: 704px;
            padding: 0;
        }
    }

    @media ${MqDesktop} {
        ${TableContainer} {
            max-width: 975px;
        }

        table {
            thead {
                th {
                    font-size: 0.875rem;
                }
            }

            tbody {
                th {
                    padding-left: 36px;
                }

                td:nth-child(2),
                td {
                    width: 182px;
                }
            }
        }
    }
`

export default NetworksCompared
